import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { useAuthStore } from '@/store/authStore'
import { Lock, Mail } from 'lucide-react'
import logoUrl from '@/assets/logo.png'

/**
 * Example unauthenticated page. Exposes a login action.
 */
export function LoginPage() {
  const login = useAuthStore((state) => state.login)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      await login(email, password)
    } catch {
      setError('Kunde inte logga in. Kontrollera dina uppgifter och försök igen.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-[#fcfcfb] transition-colors duration-300 font-sans">
      <div className="absolute top-0 left-0 right-0 h-18 bg-primary flex items-center justify-center px-6 z-40 shadow-sm">
        <div className="flex items-center justify-center">
          <img
            src={logoUrl}
            alt="Hälsogruppen Logo"
            className="h-16 w-auto object-contain"
          />
        </div>
      </div>

      <div className="z-10 w-full max-w-md px-6 animate-in fade-in slide-in-from-bottom-8 duration-700 mt-16 md:mt-24">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-serif text-primary">Logga in</h1>
        </div>

        <Card className="w-full bg-card/80 backdrop-blur-md border-border shadow-xl dark:shadow-2xl rounded-none p-8 sm:p-12">
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-6"
          >
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-[11px] font-bold uppercase tracking-widest text-primary/80"
              >
                E-postadress
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/50" />
                <Input
                  id="email"
                  type="email"
                  placeholder="namn@halsogruppen.se"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 h-12 bg-slate-50 dark:bg-zinc-900 border-slate-200 dark:border-zinc-800 rounded-none focus-visible:ring-primary focus-visible:border-primary transition-all"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-[11px] font-bold uppercase tracking-widest text-primary/80"
              >
                Lösenord
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/50" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 h-12 bg-slate-50 dark:bg-zinc-900 border-slate-200 dark:border-zinc-800 rounded-none focus-visible:ring-primary focus-visible:border-primary transition-all"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-400 text-sm border border-red-100 dark:border-red-900/50">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-accent text-white rounded-none h-12 uppercase tracking-wider font-bold transition-all duration-300 hover:bg-accent hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(249,140,2,0.4)] active:bg-accent active:translate-y-0 active:shadow-none"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                  Loggar in...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">Logga in</span>
              )}
            </Button>
          </form>
        </Card>

        <div className="text-center mt-8 text-xs text-slate-400 uppercase tracking-widest">
          &copy; {new Date().getFullYear()} Hälsogruppen Admin
        </div>
      </div>
    </main>
  )
}
